// The valley's share of the handle and the harness (gates A2.2, A2.3): its scene, view, invariants, planted-defect
// switches and the handle keys under `__rpg.controller`, `__rpg.feel` and `__rpg.bots`.
import type * as THREE from "three";
import { finite, notBelowGround, stuck, type Invariant } from "@rpg/harness";
import type { Runtime } from "@rpg/runtime";
import type { SimHost } from "../sim/host";
import { runMonkey, runRoute } from "./bots";
import { createFeel, FEEL_PROBES, type FeelProbe } from "./feel";
import { VALLEY_FAULTS, createValley, type ValleyFaultName, type ValleyRig, type ValleyVariant } from "./scene";
import { readTuning, type TuningLookup } from "./tuning";
import { createValleyView } from "./view";

export { VALLEY_FAULTS, type ValleyFaultName } from "./scene";
export const VALLEY_FAULT_NAMES = Object.keys(VALLEY_FAULTS) as ValleyFaultName[];

/** The valley scene, built from the tuning table (or a variant of it on the same machinery: the dojo). */
export function valleyScene(get: TuningLookup, rowIds: readonly string[], variant?: ValleyVariant) {
  return createValley(readTuning(get, rowIds), variant);
}

/** Switch a valley fault on or off, in the record and in the live objects it lives beside. */
export function setValleyFault(rig: ValleyRig | null, name: ValleyFaultName, on: boolean): void {
  VALLEY_FAULTS[name] = on;
  if (!rig) return;
  if (name === "noGroundSnap") rig.collision.faults.noGroundSnap = on;
  if (name === "handoffNeverReturns") rig.handoff.faults.neverReturns = on;
  if (name === "noCoyote") rig.heroCtl.faults.noCoyote = on;
}

/** The valley's invariants, added to the harness's watch: the hero never below the terrain, never non-finite, never
 *  stuck over 2 s with the stick held (unless pressing into a wall or a slope it can't climb, or the kernel game is
 *  paused: `paused`, a cutscene holding the world, phase D joined). */
export function valleyInvariants(rig: () => ValleyRig, runtime: Runtime, o: { paused?: () => boolean } = {}): Invariant[] {
  const feet = () => rig().hero().feet;
  return [
    finite("hero", () => {
      const h = rig().hero();
      return [h.feet.x, h.feet.y, h.feet.z, h.vel.x, h.vel.y, h.vel.z, h.yaw];
    }),
    notBelowGround("hero", feet, (x, z) => rig().terrainAt(x, z), { clearance: -0.05, detail: false }),
    stuck(
      "hero",
      {
        time: () => runtime.time,
        pos: feet,
        wantsToMove: () => {
          const r = rig();
          const i = r.heroInput();
          const m = i.move;
          // a move's override (combat's drive: rooted while blocking or attacking) holds the hero on purpose
          return r.heroInControl() && !i.drive && Math.hypot(m.x, m.z) > r.tuning.hero.deadZone;
        },
        held: () => {
          // a paused world (a cutscene plays) holds everyone still on purpose
          if (o.paused?.()) return true;
          // pressing into a wall, a step too high or a slope too steep holds the hero still on purpose
          const r = rig();
          const m = r.heroInput().move;
          const mag = Math.hypot(m.x, m.z);
          const move = r.heroCtl.lastMove();
          return move !== null && move.contacts.some((k) => k.normal.y < 0.7 && k.normal.x * m.x + k.normal.z * m.z < -0.2 * mag);
        },
      },
      { seconds: 2, minProgress: 0.1 },
    ),
  ];
}

/** The handle keys the valley adds. */
export function valleyHandle(rig: () => ValleyRig, host: SimHost, runtime: Runtime) {
  return {
    controller: {
      /** The hero's, the mount's (null where there is none) and the control state. */
      state: () => ({ hero: structuredClone(rig().hero()), mount: structuredClone(rig().mount()), control: structuredClone(rig().control()) }),
      /** Teleport the hero (at rest, on foot, control back home); y defaults to the terrain. */
      warp: (x: number, z: number, y?: number, yaw?: number) => rig().warpHero(x, y ?? rig().terrainAt(x, z), z, yaw),
      /** The last 500 mode changes, the hero's change counts and the camera events. */
      trace: () => ({ changes: [...rig().trace], counts: { ...rig().counts }, camera: [...rig().cameraEvents] }),
      /** Set the camera's yaw (the stick is relative to it): the rig's, when one is mounted, and the sim's. */
      setCameraYaw: (yaw: number) => {
        rig().camera?.setYaw(yaw);
        host.game.dispatch("valley/camera-yaw", yaw);
      },
      /** Keep the sim's camera yaw as set (true) instead of following the camera rig's (false, the default). */
      holdCameraYaw: (on: boolean) => void (rig().yawHeld = on),
      /** What the collision world answers at (x, z): the terrain height, the water surface, the tag underfoot of a probe dropped there. */
      probe: (x: number, z: number) => {
        const c = rig().collision;
        const hit = c.castRay({ x, y: 50, z }, { x: 0, y: -1, z: 0 }, 100);
        return { terrain: rig().terrainAt(x, z), water: c.waterSurface(x, z), top: hit ? { id: hit.id, tag: hit.tag, y: hit.point.y } : null };
      },
      /** The course as built from the tuning: steps and ramps with their heights and whether they're allowed. */
      course: () => ({ steps: rig().course.steps, ramps: rig().course.ramps }),
    },
    feel: {
      probes: () => [...FEEL_PROBES],
      /** Measure the feel table's rows (all probes by default). */
      measure: (names?: FeelProbe[]) => createFeel(rig(), runtime).measure(names),
      /** The hero's tuning as the controller uses it. */
      tuning: () => ({ ...rig().tuning.hero }),
    },
    bots: {
      /** The seeded route bot over the course, `minutes` of sim time (default 10) or a number of `laps`. */
      route: (o?: { seed?: number; minutes?: number; laps?: number }) => runRoute(rig(), runtime, o),
      /** The seeded monkey, `minutes` of sim time (default 3). */
      monkey: (o?: { seed?: number; minutes?: number }) => runMonkey(rig(), runtime, o),
    },
  };
}

/** The view (the camera is src/valley/camera.ts, on @rpg/camera). */
export function valleyView(parent: THREE.Object3D, host: SimHost): () => void {
  return createValleyView(parent, host.setup.bodies, host.collision);
}
